import { useState } from 'react';
import AuthService from '../services/AuthService';
import {
  errorText, isEmail, isValidMobile, passwordProblem,
} from '../utils/format';
import { useSession } from './SessionViewModel';

function verificationIdOf(data) {
  const id = data?.id;
  if (!id) return null;
  if (typeof id === 'string') return id;
  return id.mobileVerificationId || id.emailVerificationId || null;
}

function validLoginId(value) {
  return isEmail(value) || isValidMobile(value);
}

/** Password and OTP sign-in. Mirrors the WappGrow sign-in screen. */
export function useSignInViewModel() {
  const { setSignedIn } = useSession();
  const [mode, setMode] = useState('password');
  const [otpStep, setOtpStep] = useState('request');
  const [verificationId, setVerificationId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const changeMode = (next) => {
    setMode(next);
    setOtpStep('request');
    setVerificationId(null);
    setError('');
    setNotice('');
  };

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const signInWithPassword = ({ loginId, password }) => {
    if (!validLoginId(loginId.trim())) { setError('Enter a valid email address or mobile number.'); return Promise.resolve(); }
    if (!password) { setError('Enter your password.'); return Promise.resolve(); }
    return run(async () => setSignedIn(await AuthService.signin(loginId.trim(), password)));
  };

  const sendOtp = ({ loginId }) => {
    if (!validLoginId(loginId.trim())) { setError('Enter a valid email address or mobile number.'); return Promise.resolve(); }
    return run(async () => {
      const data = await AuthService.loginVerification(loginId.trim());
      setVerificationId(verificationIdOf(data));
      setOtpStep('verify');
      setNotice('We sent a 4 digit code. It is valid for 7 minutes.');
    });
  };

  const verifyOtp = ({ loginId, code }) => {
    if (!/^\d{4}$/.test(code)) { setError('Enter the 4 digit code.'); return Promise.resolve(); }
    return run(async () => setSignedIn(await AuthService.signinOtp(loginId.trim(), verificationId, code)));
  };

  const reportError = (message) => {
    setError(message || 'Social sign-in did not complete. Try again, or use your number and password.');
    setNotice('');
  };

  return {
    mode, otpStep, busy, error, notice, changeMode, signInWithPassword, sendOtp, verifyOtp, reportError,
  };
}

/** Sign-up: details -> mobile OTP -> account created and signed in. */
export function useSignUpViewModel() {
  const { setSignedIn } = useSession();
  const [step, setStep] = useState('details');
  const [verificationId, setVerificationId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };

  const requestCode = (form) => {
    if (!form.firstName.trim()) { setError('Enter your name.'); return Promise.resolve(); }
    if (!form.email.trim() || !isEmail(form.email)) { setError('Enter a valid email address.'); return Promise.resolve(); }
    if (!isValidMobile(form.mobile)) { setError('Enter a valid mobile number (10 to 15 digits, with country code if needed).'); return Promise.resolve(); }
    const problem = passwordProblem(form.password);
    if (problem) { setError(problem); return Promise.resolve(); }
    return run(async () => {
      const data = await AuthService.signupVerification(form.mobile.trim());
      setVerificationId(verificationIdOf(data));
      setStep('verify');
    });
  };

  const createAccount = (form, code) => {
    if (!/^\d{4}$/.test(code)) { setError('Enter the 4 digit code.'); return Promise.resolve(); }
    return run(async () => {
      const data = await AuthService.signup({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        password: form.password,
        mobile_verification_id: verificationId,
        mobile_verification_code: code,
      });
      setSignedIn(data);
    });
  };

  const reportError = (message) => {
    setError(message || 'Social sign-in did not complete. Try again, or use your number and password.');
  };

  return {
    step, busy, error, setStep, requestCode, createAccount, reportError,
  };
}

/** Forgot password: request code -> new password. */
export function useForgotPasswordViewModel() {
  const [step, setStep] = useState('request');
  const [verificationId, setVerificationId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };

  const requestCode = (loginId) => {
    if (!validLoginId(loginId.trim())) { setError('Enter a valid email address or mobile number.'); return Promise.resolve(); }
    return run(async () => {
      const data = await AuthService.requestPasswordReset(loginId.trim());
      setVerificationId(data?.verification_id || verificationIdOf(data));
      setStep('reset');
    });
  };

  const reset = ({ loginId, code, password }) => {
    if (!/^\d{4}$/.test(code)) { setError('Enter the 4 digit code.'); return Promise.resolve(); }
    const problem = passwordProblem(password);
    if (problem) { setError(problem); return Promise.resolve(); }
    return run(async () => {
      await AuthService.resetPassword({
        login_id: loginId.trim(), verification_id: verificationId, verification_code: code, password,
      });
      setDone(true);
    });
  };

  return {
    step, busy, error, done, requestCode, reset,
  };
}
